import { useState, useEffect } from 'react';
import { X, Trash2, ExternalLink, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import axios from 'axios';
import { API_URL } from '../../services/api';
import { VEHICLE_PLACEHOLDER_URL } from '../../constants/vehiclePlaceholder';
import '../AllCss/CarComparison.css';

function CarComparison({ cars, onClose, onRemove }) {
  const navigate = useNavigate();
  const toast = useToast();
  const maxCompare = 6; // Maximum cars to compare
  const [carsWithFeatures, setCarsWithFeatures] = useState(cars);

  // Fetch features for each car from backend
  useEffect(() => {
    const fetchCarFeatures = async () => {
      try {
        const carsData = await Promise.all(
          cars.map(async (car) => {
            try {
              const response = await axios.get(`${API_URL}/api/cars/${car.id || car.stockNumber}`);
              
              if (response.data.success && response.data.data?.car) {
                const fullCarData = response.data.data.car;
                
                // Merge features from backend with existing car data
                const updatedCar = {
                  ...car,
                  features: fullCarData.features || car.features || {},
                };
                return updatedCar;
              }
              return car;
            } catch (error) {
              console.error(`Error fetching features for car ${car.id}:`, error);
              return car;
            }
          })
        );
        setCarsWithFeatures(carsData);
      } catch (error) {
        console.error('Error fetching car features:', error);
        setCarsWithFeatures(cars);
      }
    };

    if (cars && cars.length > 0) {
      fetchCarFeatures();
    }
  }, [cars]);

  if (!carsWithFeatures || carsWithFeatures.length === 0) {
    return null;
  }

  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatMileage = (value) => {
    if (!value || isNaN(value)) return '—';
    return `${Number(value).toLocaleString()} mi`;
  };

  const getImage = (car) => {
    return (car.images && car.images.length > 0) 
      ? car.images[0] 
      : VEHICLE_PLACEHOLDER_URL;
  };

  const handleViewDetails = (car) => {
    navigate(`/cars/${car.stockNumber || car.id}`);
    onClose();
  };

  const handleShareComparison = async () => {
    const ids = carsWithFeatures.map((c) => c.id).filter(Boolean).join(',');
    const url = `${window.location.origin}/inventory?compare=${ids}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Comparison link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div className="comparison-overlay" onClick={onClose}>
      <div className="comparison-modal" onClick={(e) => e.stopPropagation()}>
        <div className="comparison-header">
          <h2>Compare Cars</h2>
          <div className="comparison-header-actions">
            <button type="button" className="comparison-share" onClick={handleShareComparison}>
              <Share2 size={16} aria-hidden />
              Share
            </button>
            <button type="button" className="comparison-close" onClick={onClose} aria-label="Close comparison">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="comparison-content">
          <div className="comparison-table">
            {/* Header Row - Car Images and Basic Info */}
            <div className="comparison-row comparison-header-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Vehicle</span>
              </div>
              {carsWithFeatures.map((car, index) => (
                <div key={car.id} className={`comparison-cell comparison-car-cell ${index === 0 ? 'comparison-car-cell-sticky' : ''}`}>
                  <button
                    className="comparison-remove-btn"
                    onClick={() => onRemove(car.id)}
                    title="Remove from comparison"
                  >
                    <Trash2 size={12} />
                  </button>
                  <div className="comparison-car-image">
                    <img src={getImage(car)} alt={`${car.year} ${car.make} ${car.model}`} />
                  </div>
                  <div className="comparison-car-title">
                    <h3>{car.year} {car.make} {car.model}</h3>
                    {car.trim && <p className="comparison-car-trim">{car.trim}</p>}
                    <p className="comparison-car-stock">Stock #: {car.stockNumber || '—'}</p>
                  </div>
                  <button
                    className="comparison-view-btn"
                    onClick={() => handleViewDetails(car)}
                  >
                    View Details <ExternalLink size={12} />
                  </button>
                </div>
              ))}
            </div>

            {/* Price Row */}
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Price</span>
              </div>
              {carsWithFeatures.map((car, index) => (
                <div key={car.id} className={`comparison-cell ${index === 0 ? 'comparison-data-cell-sticky' : ''}`}>
                  <div className="comparison-price">{formatCurrency(car.price)}</div>
                  {car.isPriceDrop && car.originalPrice && (
                    <div className="comparison-original-price">
                      Was {formatCurrency(car.originalPrice)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Year Row */}
            {carsWithFeatures.some(car => car.year) && (
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Year</span>
              </div>
                {carsWithFeatures.map((car) => (
                <div key={car.id} className="comparison-cell">
                    {car.year || '—'}
                </div>
              ))}
            </div>
            )}

            {/* Mileage Row */}
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Mileage</span>
              </div>
              {carsWithFeatures.map((car, index) => (
                <div key={car.id} className={`comparison-cell ${index === 0 ? 'comparison-data-cell-sticky' : ''}`}>
                  {formatMileage(car.mileage)}
                </div>
              ))}
            </div>

            {/* Body Type Row */}
            {carsWithFeatures.some(car => car.bodyType) && (
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Body Type</span>
              </div>
                {carsWithFeatures.map((car) => (
                <div key={car.id} className="comparison-cell">
                    {car.bodyType ? car.bodyType.charAt(0).toUpperCase() + car.bodyType.slice(1) : '—'}
                </div>
              ))}
            </div>
            )}

            {/* Fuel Type Row */}
            {carsWithFeatures.some(car => car.fuelType) && (
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Fuel Type</span>
              </div>
                {carsWithFeatures.map((car) => (
                <div key={car.id} className="comparison-cell">
                    {car.fuelType ? car.fuelType.charAt(0).toUpperCase() + car.fuelType.slice(1) : '—'}
                </div>
              ))}
            </div>
            )}

            {/* Transmission Row */}
            {carsWithFeatures.some(car => car.transmission) && (
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Transmission</span>
              </div>
                {carsWithFeatures.map((car) => (
                <div key={car.id} className="comparison-cell">
                    {car.transmission || '—'}
                </div>
              ))}
            </div>
            )}

            {/* Drivetrain Row */}
            {carsWithFeatures.some(car => car.drivetrain) && (
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Drivetrain</span>
              </div>
                {carsWithFeatures.map((car) => (
                <div key={car.id} className="comparison-cell">
                    {car.drivetrain || '—'}
                </div>
              ))}
            </div>
            )}

            {/* Engine Row */}
            {carsWithFeatures.some(car => car.engineType) && (
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Engine</span>
              </div>
                {carsWithFeatures.map((car) => (
                <div key={car.id} className="comparison-cell">
                    {car.engineType || '—'}
                </div>
              ))}
            </div>
            )}

            {/* Seats Row */}
            {carsWithFeatures.some(car => car.seats) && (
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Seats</span>
              </div>
                {carsWithFeatures.map((car) => (
                <div key={car.id} className="comparison-cell">
                    {car.seats || '—'}
                </div>
              ))}
            </div>
            )}

            {/* Exterior Color Row */}
            {carsWithFeatures.some(car => car.exteriorColor) && (
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Exterior Color</span>
              </div>
                {carsWithFeatures.map((car) => (
                <div key={car.id} className="comparison-cell">
                    {car.exteriorColor || '—'}
                </div>
              ))}
            </div>
            )}

            {/* Interior Color Row */}
            {carsWithFeatures.some(car => car.interiorColor) && (
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Interior Color</span>
                </div>
                {carsWithFeatures.map((car) => (
                  <div key={car.id} className="comparison-cell">
                    {car.interiorColor || '—'}
                  </div>
                ))}
              </div>
            )}

            {/* MPG City Row */}
            {carsWithFeatures.some(car => car.mpgCity) && (
              <div className="comparison-row">
                <div className="comparison-cell comparison-label-cell">
                  <span>MPG City</span>
                </div>
                {carsWithFeatures.map((car) => (
                <div key={car.id} className="comparison-cell">
                    {car.mpgCity || '—'}
                  </div>
                ))}
              </div>
            )}

            {/* MPG Highway Row */}
            {carsWithFeatures.some(car => car.mpgHighway) && (
              <div className="comparison-row">
                <div className="comparison-cell comparison-label-cell">
                  <span>MPG Highway</span>
                </div>
                {carsWithFeatures.map((car) => (
                  <div key={car.id} className="comparison-cell">
                    {car.mpgHighway || '—'}
                </div>
              ))}
            </div>
            )}

            {/* Features Row - Always show at bottom */}
            <div className="comparison-row">
              <div className="comparison-cell comparison-label-cell">
                <span>Features</span>
              </div>
              {carsWithFeatures.map((car) => {
                let allFeatures = [];
                
                if (car.features) {
                  if (Array.isArray(car.features)) {
                    allFeatures = car.features;
                  } else if (typeof car.features === 'object') {
                    allFeatures = Object.values(car.features).flat().filter(Boolean);
                  }
                }
                
                return (
                <div key={car.id} className="comparison-cell">
                    {allFeatures.length > 0 ? (
                    <ul className="comparison-features-list">
                        {allFeatures.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  ) : (
                      '—'
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="comparison-footer">
          <p className="comparison-note">
            Compare up to {maxCompare} vehicles at once
          </p>
        </div>
      </div>
    </div>
  );
}

export default CarComparison;

